import { addMonths, computeDueObligations, computeGaps, parseIsoDate, resolveCurrentLevels } from "./gap"
import { catalogIndex } from "./catalog-index"
import { bausteinCostUsd } from "./economics"
import { byId, byText, scoreKey } from "./order"
import { DEFAULT_POLICY, finiteOr, resolvePolicy } from "./policy"
import type { ResolvedPolicy } from "./policy"
import type {
    Baustein,
    Catalog,
    Constraints,
    Curriculum,
    CurriculumItem,
    CurriculumItemReason,
    DueObligation,
    EmployeeProfile,
    Gap,
    Modality,
    QuarterPlan,
    SkillLevel,
} from "./types"

/**
 * Steps 4+5: match Bausteine to gaps, then compose them into a budgeted,
 * sequenced programme.
 *
 * DETERMINISTIC. Same profile + same catalog + same constraints ⇒ byte-identical
 * curriculum. That property is the reason a regulator-relevant training plan can
 * be defended at all — and it is asserted directly in the tests.
 */

// ---------------------------------------------------------------------------
// Budget
// ---------------------------------------------------------------------------

/**
 * Effective learning hours per quarter after Pensum scaling.
 *
 * This one line is the demo's most persuasive moment (plan §4): moving a
 * Kundenberaterin from 100% to 60% visibly shrinks the budget, developmental
 * items fall away, and every mandatory item survives. Keeping the scaling in
 * the engine rather than the UI is what makes that real rather than theatre.
 */
export function effectiveQuarterBudget(
    pensum: number,
    hoursAtFullTime: number,
    scaleByPensum = true,
): number {
    // The rounding stays OUTSIDE the branch on purpose: both paths must round
    // identically, or turning Pensum scaling off would silently change the
    // budget of a full-time employee by a tenth of an hour.
    return Math.round(hoursAtFullTime * (scaleByPensum ? pensum / 100 : 1) * 10) / 10
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

interface Candidate {
    baustein: Baustein
    closesGaps: Gap[]
    /** Higher is better. */
    score: number
}

/**
 * Bausteine that would close at least one open gap.
 *
 * Level-awareness lives here: a Baustein whose entry `level` is more than one
 * step below what the employee already holds in the taught skill is discarded.
 * That is what stops "Kreditanalyse: Grundlagen" being proposed to a fifteen-
 * year Kreditanalyst — the demo case that proves the system is not a keyword
 * matcher.
 */
export function findCandidates(
    gaps: Gap[],
    catalog: Catalog,
    currentLevels: Map<string, { level: SkillLevel }>,
    policy: ResolvedPolicy = DEFAULT_POLICY,
): Candidate[] {
    const gapBySkill = new Map(gaps.map((g) => [g.skillId, g]))
    const candidates: Candidate[] = []
    const { originWeight, denominator, minHours, minUsd, tooBasicTolerance } = policy.ranking

    for (const baustein of catalog.bausteine) {
        const closes: Gap[] = []
        let tooBasic = false

        for (const taught of baustein.teaches) {
            const gap = gapBySkill.get(taught.skillId)
            if (!gap) continue
            const lift = taught.level - gap.current
            // Only counts if it actually lifts the employee far enough. Over an
            // integer scale the default `minLift` of 1 is exactly the old
            // `taught.level <= gap.current`.
            if (lift < policy.gaps.minLift) {
                tooBasic = true
                continue
            }
            // Cognitive-load guard: refuse to credit a single Baustein with a
            // jump the learner could not plausibly make in one step. The default
            // of 4 is the whole scale, so it never fires.
            if (lift > policy.gaps.maxLevelJump) continue
            closes.push(gap)
        }

        if (closes.length === 0) continue

        // Discard a Baustein that sits clearly below the employee's demonstrated
        // level in every skill it teaches.
        const heldLevels = baustein.teaches
            .map((t) => currentLevels.get(t.skillId)?.level ?? 0)
            .filter((level) => level > 0)
        const maxHeld = heldLevels.length > 0 ? Math.max(...heldLevels) : 0
        if (tooBasic && closes.length === 0) continue
        if (maxHeld - baustein.level > tooBasicTolerance) continue

        // Score: value of the gaps closed, per unit of whatever is scarce.
        // `hours` is the default and reads as "best use of the learner's time";
        // `chf` is Phillips ROI and reads as "best use of the budget"; `flat`
        // ranks on raw value and exists mainly to show what the denominator was
        // doing all along.
        const value = closes.reduce((sum, gap) => sum + originWeight[gap.origin] * gap.size, 0)
        const cost =
            denominator === "usd"
                ? Math.max(bausteinCostUsd(baustein, policy), minUsd)
                : denominator === "flat"
                  ? 1
                  : Math.max(baustein.durationHours, minHours)

        candidates.push({
            baustein,
            closesGaps: closes,
            // `finiteOr` is not defensive dressing: a zero-cost format under the
            // USD denominator is exactly how 0/0 gets in, and a NaN comparator
            // is treated as 0 by V8 — which makes the sort order an artefact of
            // the array length rather than of the policy.
            score: finiteOr(value / cost, 0),
        })
    }

    return candidates.sort(
        (a, b) =>
            scoreKey(b.score) - scoreKey(a.score) ||
            byText(a.baustein.title, b.baustein.title) ||
            byId(a.baustein.id, b.baustein.id),
    )
}

// ---------------------------------------------------------------------------
// Prerequisites
// ---------------------------------------------------------------------------

/**
 * Expand a Baustein into itself plus every unmet prerequisite, in dependency
 * order. Cycles are impossible in the catalog but guarded anyway — an infinite
 * loop in a demo is worse than a missing prerequisite.
 */
export function expandPrerequisites(
    baustein: Baustein,
    catalog: Catalog,
    completed: Set<string>,
    policy: ResolvedPolicy = DEFAULT_POLICY,
    seen: Set<string> = new Set(),
    /**
     * Include `baustein` itself even though it is in `completed`.
     *
     * Set for the TARGET of a due obligation: a refresher that was taken last
     * year is "completed" and simultaneously "due again", and treating the
     * completion as final silently drops the single most important item in the
     * plan. Prerequisites keep the normal completed-check — those really are
     * done once.
     */
    forceSelf = false,
    depth = 0,
): Baustein[] {
    if (seen.has(baustein.id)) return []
    if (completed.has(baustein.id) && !forceSelf) return []
    seen.add(baustein.id)

    const chain: Baustein[] = []
    // Expansion off, or the depth budget spent: emit the Baustein alone. The
    // deepest chain in the catalog is 2, so the default of 8 never bites — the
    // limit exists so a curated catalog cannot produce a plan that is nothing
    // but prerequisites.
    if (policy.scheduling.prerequisites.expand && depth < policy.scheduling.prerequisites.maxDepth) {
        const { bausteinById } = catalogIndex(catalog)
        for (const prerequisiteId of baustein.prerequisites) {
            const prerequisite = bausteinById.get(prerequisiteId)
            if (!prerequisite) continue
            chain.push(
                ...expandPrerequisites(
                    prerequisite,
                    catalog,
                    completed,
                    policy,
                    seen,
                    false,
                    depth + 1,
                ),
            )
        }
    }
    chain.push(baustein)
    return chain
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

function quarterLabel(asOf: string, index: number, periodMonths = 3): string {
    const start = addMonths(asOf, index * periodMonths)
    const year = Number(start.slice(0, 4))
    const month = Number(start.slice(5, 7))
    // Only a three-month period is a quarter. Any other length gets a month
    // label, because "Q2" for a two-month window would simply be wrong.
    if (periodMonths === 3) return `Q${Math.floor((month - 1) / 3) + 1} ${year}`
    return `${String(month).padStart(2, "0")}/${year}`
}

/**
 * The composer.
 *
 * Order of operations is the policy:
 *   1. Obligations first, placed by deadline. NEVER dropped, even over budget —
 *      an over-budget quarter is a visible planning problem; a silently dropped
 *      compliance duty is a finding.
 *   2. Then role gaps, then MAG-derived, then aspiration — by candidate score.
 *   3. Anything that does not fit inside the horizon goes to `deferred`, not to
 *      /dev/null. "Kein stiller Verlust" is what lets a manager trust the plan.
 */
export function composeCurriculum(
    profile: EmployeeProfile,
    catalog: Catalog,
    constraints: Constraints,
): Curriculum {
    return planFor(profile, catalog, constraints).curriculum
}

/**
 * Everything the pipeline derives about one person, computed once.
 *
 * `composeCurriculum` already needs the gaps and the due obligations, so a
 * caller that wants to display them alongside the plan — the L&D cockpit does,
 * for all 1,960 employees — was making the engine derive each of them twice and
 * `resolveCurrentLevels` three times. Same answers, ~40% of the work.
 */
export function planFor(
    profile: EmployeeProfile,
    catalog: Catalog,
    constraints: Constraints,
): { curriculum: Curriculum; gaps: Gap[]; dueObligations: DueObligation[] } {
    const policy = resolvePolicy(constraints.policy)
    const gaps = computeGaps(profile, catalog, constraints)
    const dueObligations = computeDueObligations(profile, catalog, constraints)
    const currentLevels = resolveCurrentLevels(profile, catalog, constraints)
    const completed = new Set(profile.completed.map((c) => c.bausteinId))
    const { bausteinById, skillById } = catalogIndex(catalog)

    const periodMonths = policy.budget.periodMonths
    const quarterCount = Math.max(1, Math.ceil(constraints.horizonMonths / periodMonths))
    const budgetPerQuarter = effectiveQuarterBudget(
        profile.pensum,
        constraints.hoursPerQuarterAtFullTime,
        policy.budget.scaleByPensum,
    )

    const quarters: QuarterPlan[] = Array.from({ length: quarterCount }, (_, index) => ({
        index,
        label: quarterLabel(constraints.asOf, index, periodMonths),
        budgetHours: budgetPerQuarter,
        plannedHours: 0,
        items: [],
    }))

    const items: CurriculumItem[] = []
    const deferred: CurriculumItem[] = []
    const placed = new Set<string>()

    /** Quarter index a deadline falls into, clamped to the horizon. */
    const quarterForDeadline = (dueBy: string): number => {
        const asOfMs = parseIsoDate(constraints.asOf)
        const months = Math.floor((parseIsoDate(dueBy) - asOfMs) / (86_400_000 * 30.44))
        return Math.min(quarterCount - 1, Math.max(0, Math.floor(months / periodMonths)))
    }

    /**
     * The three load limits, checked together.
     *
     * All three default to values the catalog cannot reach (999 items, 999
     * hours, 9 categories against a 51-Baustein catalog whose longest course is
     * 16 h and which spans 5 categories), so this is inert until an L&D team
     * turns it on. `maxCategoriesPerPeriod` is the interleaving limit: learning
     * four unrelated subjects at once is worse than learning two, twice.
     */
    const categoriesIn = (quarter: QuarterPlan, extra?: Baustein): Set<string> => {
        const categories = new Set<string>()
        for (const existing of [...quarter.items.map((i) => i.baustein), ...(extra ? [extra] : [])]) {
            for (const taught of existing.teaches) {
                const category = skillById.get(taught.skillId)?.category
                if (category) categories.add(category)
            }
        }
        return categories
    }

    const withinLimits = (quarter: QuarterPlan, baustein: Baustein): boolean =>
        quarter.items.length < policy.scheduling.maxItemsPerPeriod &&
        baustein.durationHours <= policy.scheduling.maxHoursPerItem &&
        categoriesIn(quarter, baustein).size <= policy.scheduling.maxCategoriesPerPeriod

    const place = (item: CurriculumItem, preferredQuarter: number, force: boolean): boolean => {
        const hours = item.baustein.durationHours
        // Try the preferred quarter, then earlier ones (never later than a
        // deadline), then — for forced items only — the preferred one regardless.
        for (let index = preferredQuarter; index >= 0; index--) {
            const quarter = quarters[index]
            // A forced item is a legal duty. It ignores the load limits for the
            // same reason it ignores the budget: an overloaded quarter is a
            // visible planning problem, a dropped duty is an audit finding.
            if (!force && !withinLimits(quarter, item.baustein)) continue
            if (quarter.plannedHours + hours <= quarter.budgetHours) {
                quarter.plannedHours += hours
                quarter.items.push({ ...item, quarter: index })
                items.push({ ...item, quarter: index })
                placed.add(item.baustein.id)
                return true
            }
        }
        if (!force) return false

        const quarter = quarters[preferredQuarter]
        quarter.plannedHours += hours
        quarter.items.push({ ...item, quarter: preferredQuarter })
        items.push({ ...item, quarter: preferredQuarter })
        placed.add(item.baustein.id)
        return true
    }

    // --- 1. Obligations ----------------------------------------------------
    for (const due of dueObligations) {
        const baustein = bausteinById.get(due.obligation.bausteinId)
        if (!baustein) continue

        const chain = expandPrerequisites(baustein, catalog, completed, policy, new Set(), true)
        const targetQuarter = quarterForDeadline(due.dueBy)

        for (const step of chain) {
            if (placed.has(step.id)) continue
            const isTarget = step.id === baustein.id
            const reason: CurriculumItemReason = isTarget ? "pflicht" : "voraussetzung"
            place(
                {
                    baustein: step,
                    reason,
                    quarter: targetQuarter,
                    dueBy: isTarget ? due.dueBy : null,
                    closesGaps: gaps.filter((gap) =>
                        step.teaches.some((t) => t.skillId === gap.skillId && t.level > gap.current),
                    ),
                    ...(isTarget ? { dischargesObligation: due.obligation } : {}),
                    ...(isTarget ? {} : { prerequisiteFor: [baustein.id] }),
                },
                // Prerequisites must land no later than the item they unlock.
                isTarget ? targetQuarter : Math.max(0, targetQuarter - 1),
                // Force: a mandatory chain always gets placed, budget or not.
                // Turning this off is a real governance decision, and the
                // Steuerung page labels it as one — a duty that silently fails
                // to fit becomes an audit finding rather than a planning problem.
                policy.obligations.neverDrop,
            )
        }
    }

    // --- 2. Gap-driven development ------------------------------------------
    /**
     * Skills already lifted to target by something in the plan.
     *
     * Without this the composer keeps proposing Bausteine for gaps it has
     * already closed: Luca's `fach.zahlungsverkehr` gap was covered by the 3h
     * "Zahlungsverkehr" course and then covered AGAIN by "Firmenkundengeschäft"
     * (8h) — which also dragged in "Kreditanalyse: Grundlagen" (8h) as a
     * prerequisite. 16 wasted hours, and an apprentice staring at a credit
     * course. A plan is a curated set, not everything that matched.
     */
    const satisfied = new Map<string, SkillLevel>()
    const recordSatisfied = (baustein: Baustein) => {
        for (const taught of baustein.teaches) {
            const best = satisfied.get(taught.skillId) ?? 0
            if (taught.level > best) satisfied.set(taught.skillId, taught.level)
        }
    }
    // Seed from the obligation pass — its items count as covering their skills.
    for (const item of items) recordSatisfied(item.baustein)
    const stillOpen = (gap: Gap) => (satisfied.get(gap.skillId) ?? 0) < gap.required

    /**
     * 70-20-10, as proportional steering rather than a quota.
     *
     * A quota would drop a needed course to hit a percentage, which is
     * indefensible. Instead each candidate's score is multiplied when its
     * modality is currently under its target share, so the plan drifts toward
     * the mix while ranking still decides. With `mix.enabled` off — the default
     * — `nextIndex` is always 0 and this reduces exactly to walking the
     * pre-sorted candidate list.
     */
    const plannedHoursByModality = (): Record<Modality, number> => {
        const totals: Record<Modality, number> = { "on-the-job": 0, social: 0, formal: 0 }
        for (const item of items) {
            totals[policy.mix.modalityOf[item.baustein.format]] += item.baustein.durationHours
        }
        return totals
    }

    const nextIndex = (pending: Candidate[]): number => {
        if (!policy.mix.enabled || pending.length <= 1) return 0
        const totals = plannedHoursByModality()
        const planned = totals["on-the-job"] + totals.social + totals.formal
        let bestIndex = 0
        let bestKey = Number.NEGATIVE_INFINITY
        for (let index = 0; index < pending.length; index++) {
            const modality = policy.mix.modalityOf[pending[index].baustein.format]
            // Before anything is planned every modality is under target, so the
            // boost is uniform and ranking alone decides the first pick.
            const share = planned === 0 ? 0 : (totals[modality] / planned) * 100
            const under = share < policy.mix.target[modality]
            const key = scoreKey(pending[index].score * (under ? policy.mix.strength : 1))
            if (
                key > bestKey ||
                (key === bestKey && byId(pending[index].baustein.id, pending[bestIndex].baustein.id) < 0)
            ) {
                bestKey = key
                bestIndex = index
            }
        }
        return bestIndex
    }

    const pending = findCandidates(gaps, catalog, currentLevels, policy)
    while (pending.length > 0) {
        const candidate = pending.splice(nextIndex(pending), 1)[0]
        if (placed.has(candidate.baustein.id)) continue

        // Re-check against what is already planned, not just against the
        // original gap list — earlier iterations of this same loop count too.
        const openGaps = candidate.closesGaps.filter(stillOpen)
        if (openGaps.length === 0) continue
        candidate.closesGaps = openGaps

        const chain = expandPrerequisites(candidate.baustein, catalog, completed, policy).filter(
            (step) => !placed.has(step.id),
        )
        const chainHours = chain.reduce((sum, step) => sum + step.durationHours, 0)
        const remaining = quarters.reduce(
            (sum, quarter) => sum + Math.max(0, quarter.budgetHours - quarter.plannedHours),
            0,
        )

        const dominantOrigin = candidate.closesGaps[0]?.origin ?? "rolle"
        const reason: CurriculumItemReason = dominantOrigin === "ziel" ? "ziel" : "luecke"

        // `chain-vs-remaining` asks "does the whole chain fit in what is left
        // anywhere in the horizon?" and defers early if not — which keeps a plan
        // from being half a chain. `period-only` skips that check and lets the
        // per-period placement below decide, producing fuller near quarters and
        // more partial chains.
        const doesNotFit =
            policy.scheduling.deferralMode === "chain-vs-remaining" && chainHours > remaining
        if (doesNotFit) {
            deferred.push({
                baustein: candidate.baustein,
                reason,
                quarter: -1,
                dueBy: null,
                closesGaps: candidate.closesGaps,
            })
            continue
        }

        // Place the chain front-to-back so prerequisites never land after the
        // Baustein they unlock.
        let cursor = 0
        for (const step of chain) {
            const isTarget = step.id === candidate.baustein.id
            const item: CurriculumItem = {
                baustein: step,
                reason: isTarget ? reason : "voraussetzung",
                quarter: cursor,
                dueBy: null,
                closesGaps: isTarget
                    ? candidate.closesGaps
                    : gaps.filter((gap) =>
                          step.teaches.some((t) => t.skillId === gap.skillId && t.level > gap.current),
                      ),
                ...(isTarget ? {} : { prerequisiteFor: [candidate.baustein.id] }),
            }

            let landed = false
            for (let index = cursor; index < quarterCount; index++) {
                const quarter = quarters[index]
                if (!withinLimits(quarter, step)) continue
                if (quarter.plannedHours + step.durationHours <= quarter.budgetHours) {
                    quarter.plannedHours += step.durationHours
                    quarter.items.push({ ...item, quarter: index })
                    items.push({ ...item, quarter: index })
                    placed.add(step.id)
                    recordSatisfied(step)
                    cursor = index
                    landed = true
                    break
                }
            }
            if (!landed) {
                deferred.push({ ...item, quarter: -1 })
                break
            }
        }
    }

    // --- 3. Spaced reinforcement (Ebbinghaus) ------------------------------
    /**
     * A short booster some weeks after the real thing.
     *
     * The forgetting curve is the oldest reliable finding in the field and the
     * one corporate catalogues most consistently ignore: a duty is discharged,
     * nothing follows, and twelve months later the refresher re-teaches from
     * zero. A booster is a synthetic item, not a catalogue entry — there is no
     * course to buy, it is a scheduled act of retrieval.
     *
     * It carries the source Baustein as a prerequisite so that the composer's
     * "never schedule a prerequisite after the item it unlocks" invariant keeps
     * holding for it, and it is placed only where the budget genuinely has room:
     * a booster that pushed a duty over budget would invert its own purpose.
     */
    if (policy.reinforcement.enabled) {
        const periodDays = periodMonths * 30.44
        const periodOf = (quarterIndex: number, extraDays: number) =>
            Math.floor((quarterIndex * periodDays + extraDays) / periodDays)

        for (const source of [...items]) {
            if (!policy.reinforcement.appliesTo[source.reason]) continue
            if (source.reason === "auffrischung") continue // never a booster of a booster

            // A 30-day gap does not cross a quarter boundary, and that is fine:
            // the spacing is real even when the calendar column is the same one.
            // Clamping to `source.quarter` rather than demanding a LATER period
            // is the difference between a feature that works and one that
            // silently never fires — which is what the knob matrix caught.
            const target = Math.min(
                quarterCount - 1,
                Math.max(source.quarter, periodOf(source.quarter, policy.reinforcement.afterDays)),
            )

            const booster: Baustein = {
                ...source.baustein,
                id: `${source.baustein.id}#auffrischung`,
                durationHours: policy.reinforcement.durationHours,
                prerequisites: [source.baustein.id],
                teaches: [],
                format: "selbststudium",
            }
            const quarter = quarters[target]
            if (quarter.plannedHours + booster.durationHours > quarter.budgetHours) continue

            const item: CurriculumItem = {
                baustein: booster,
                reason: "auffrischung",
                quarter: target,
                dueBy: null,
                closesGaps: [],
            }
            quarter.plannedHours += booster.durationHours
            quarter.items.push(item)
            items.push(item)
        }
    }

    // --- 4. Uncovered gaps (the L&D catalog-gap signal) ---------------------
    const coveredSkills = new Set(
        items.flatMap((item) => item.baustein.teaches.map((t) => t.skillId)),
    )
    const { teachableSkills } = catalogIndex(catalog)
    const uncoveredGaps = gaps.filter(
        (gap) => !coveredSkills.has(gap.skillId) && !teachableSkills.has(gap.skillId),
    )

    const { reasonRank } = policy.ranking
    for (const quarter of quarters) {
        quarter.items.sort(
            (a, b) =>
                reasonRank[a.reason] - reasonRank[b.reason] ||
                byText(a.baustein.title, b.baustein.title) ||
                byId(a.baustein.id, b.baustein.id),
        )
    }

    return {
        curriculum: {
            employeeId: profile.id,
            asOf: constraints.asOf,
            horizonMonths: constraints.horizonMonths,
            budgetHoursPerQuarter: budgetPerQuarter,
            quarters,
            items,
            uncoveredGaps,
            deferred,
            totalHours:
                Math.round(items.reduce((sum, i) => sum + i.baustein.durationHours, 0) * 10) / 10,
        },
        gaps,
        dueObligations,
    }
}
